/*
 * Threshold gifts for the MH product template.
 *
 * The gift line itself is priced by a Shopify automatic "Buy X get Y" discount with a minimum
 * purchase amount, so the free price is enforced server side. This script only owns the cart
 * line: it grants the gift once the qualifying subtotal reaches the threshold and pulls the
 * line back out as soon as the subtotal drops below it, which is also when Shopify stops
 * applying the discount.
 *
 * Rules are cached in sessionStorage because the gift must be revoked from any page, while the
 * block that defines it only renders on the product page.
 */
(function () {
	var RULES_KEY = "mhGiftRules";
	var DECLINED_KEY = "mhGiftDeclined";
	var GIFT_PROPERTY = "_mh_gift";
	var THRESHOLD_PROPERTY = "_mh_gift_threshold";
	var MAX_CHAINED_MUTATIONS = 4;

	var busy = false;
	var knownPresent = null;
	var chainedMutations = 0;
	var chainResetTimer = null;

	function readStore(key, fallback) {
		try {
			var raw = sessionStorage.getItem(key);
			return raw ? JSON.parse(raw) : fallback;
		} catch (e) {
			return fallback;
		}
	}

	function writeStore(key, value) {
		try {
			sessionStorage.setItem(key, JSON.stringify(value));
		} catch (e) {}
	}

	function rulesFromDom() {
		var cards = Array.prototype.slice.call(document.querySelectorAll("[data-mh-pd-gift]"));
		return cards.reduce(function (rules, card) {
			var variantId = parseInt(card.getAttribute("data-gift-variant-id"), 10);
			var threshold = parseInt(card.getAttribute("data-threshold"), 10) || 0;
			var id = card.getAttribute("data-gift-id");
			if (!id || !variantId || threshold < 0) return rules;
			if (card.getAttribute("data-gift-auto-add") !== "true") return rules;
			rules.push({ id: id, variantId: variantId, threshold: threshold });
			return rules;
		}, []);
	}

	function mergeRules() {
		var stored = readStore(RULES_KEY, []);
		var fresh = rulesFromDom();
		if (!fresh.length) return stored;
		var byId = {};
		stored.concat(fresh).forEach(function (rule) {
			byId[rule.id] = rule;
		});
		var merged = Object.keys(byId).map(function (id) {
			return byId[id];
		});
		writeStore(RULES_KEY, merged);
		return merged;
	}

	function declinedIds() {
		return readStore(DECLINED_KEY, []);
	}

	function decline(id) {
		var list = declinedIds();
		if (list.indexOf(id) !== -1) return;
		list.push(id);
		writeStore(DECLINED_KEY, list);
	}

	function giftLines(cart) {
		return (cart.items || []).filter(function (item) {
			return !!(item.properties && item.properties[GIFT_PROPERTY]);
		});
	}

	function qualifyingSubtotal(cart) {
		var giftTotal = giftLines(cart).reduce(function (sum, item) {
			return sum + (item.final_line_price || 0);
		}, 0);
		return Math.max(0, (cart.total_price || 0) - giftTotal);
	}

	function lineIndexOf(cart, item) {
		return (cart.items || []).indexOf(item) + 1;
	}

	function postJson(url, payload) {
		return fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify(payload),
		}).then(function (res) {
			return res.json().then(function (data) {
				if (!res.ok) throw data;
				return data;
			});
		});
	}

	// refresh(false) redraws the drawer without opening it and re-broadcasts mh:cart:updated.
	function announceChange() {
		var drawer = document.querySelector("mh-cart-drawer");
		if (drawer && typeof drawer.refresh === "function") return drawer.refresh(false);
		return fetch("/cart.js")
			.then(function (res) {
				return res.json();
			})
			.then(function (cart) {
				document.dispatchEvent(new CustomEvent("mh:cart:updated", { detail: { cart: cart } }));
			})
			.catch(function () {});
	}

	function noteChainedMutation() {
		chainedMutations += 1;
		if (chainResetTimer) window.clearTimeout(chainResetTimer);
		chainResetTimer = window.setTimeout(function () {
			chainedMutations = 0;
		}, 5000);
	}

	function changeGiftLine(cart, item, quantity) {
		var line = lineIndexOf(cart, item);
		function send(payload) {
			return postJson("/cart/change.js", payload);
		}
		if (item && item.key) {
			return send({ id: String(item.key), quantity: quantity }).catch(function (error) {
				if (line < 1) throw error;
				return send({ line: line, quantity: quantity });
			});
		}
		if (line < 1) return Promise.resolve({});
		return send({ line: line, quantity: quantity });
	}

	function sync(cart) {
		if (busy) return Promise.resolve();
		if (chainedMutations >= MAX_CHAINED_MUTATIONS) return Promise.resolve();

		var rules = mergeRules();
		var gifts = giftLines(cart);
		var present = gifts.map(function (item) {
			return String(item.properties[GIFT_PROPERTY]);
		});

		// A gift that vanished while still affordable was dropped by the customer, not by us.
		if (knownPresent) {
			var subtotalNow = qualifyingSubtotal(cart);
			knownPresent.forEach(function (id) {
				if (present.indexOf(id) !== -1) return;
				var rule = rules.filter(function (r) {
					return r.id === id;
				})[0];
				if (rule && subtotalNow >= rule.threshold) decline(id);
			});
		}
		knownPresent = present;

		var subtotal = qualifyingSubtotal(cart);
		var declined = declinedIds();
		// A gift is never the only thing in the cart.
		var paidLines = (cart.items || []).length - gifts.length;

		var toRemove = gifts.filter(function (item) {
			if (paidLines === 0) return true;
			var threshold = parseInt(item.properties[THRESHOLD_PROPERTY], 10) || 0;
			return subtotal < threshold;
		});
		if (toRemove.length) {
			// Removing clears our record so the gift can be granted again later in the session.
			knownPresent = null;
			return mutate(changeGiftLine(cart, toRemove[0], 0));
		}

		var overQuantity = gifts.filter(function (item) {
			return item.quantity > 1;
		});
		if (overQuantity.length) {
			return mutate(changeGiftLine(cart, overQuantity[0], 1));
		}

		var toGrant = paidLines === 0
			? []
			: rules.filter(function (rule) {
					if (subtotal < rule.threshold) return false;
					if (declined.indexOf(rule.id) !== -1) return false;
					return present.indexOf(rule.id) === -1;
			  });
		if (toGrant.length) {
			var rule = toGrant[0];
			var properties = {};
			properties[GIFT_PROPERTY] = rule.id;
			properties[THRESHOLD_PROPERTY] = String(rule.threshold);
			return mutate(
				postJson("/cart/add.js", { id: rule.variantId, quantity: 1, properties: properties }).catch(function (error) {
					// Sold out or otherwise unavailable — stop retrying on every cart change.
					decline(rule.id);
					throw error;
				}),
			);
		}

		return Promise.resolve();
	}

	function mutate(request) {
		busy = true;
		noteChainedMutation();
		return request
			.catch(function () {})
			.then(function () {
				busy = false;
				return announceChange();
			});
	}

	function syncFromServer() {
		if (busy) return;
		fetch("/cart.js")
			.then(function (res) {
				return res.json();
			})
			.then(sync)
			.catch(function () {});
	}

	document.addEventListener("mh:cart:updated", function (event) {
		var cart = event.detail && event.detail.cart;
		if (cart && cart.items) {
			sync(cart);
		} else {
			syncFromServer();
		}
	});

	window.mhGiftRewards = { sync: syncFromServer, decline: decline };

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", syncFromServer);
	} else {
		syncFromServer();
	}
})();
